/**
 * session-cache.ts — localStorage-backed session cache with 300 s TTL.
 *
 * Goal: eliminate the 200–350 ms `GET /api/auth/get-session` cold-load wait
 * that blocks first paint on every warm boot. When a fresh cache hit exists
 * the app renders immediately with the cached session and fires a background
 * revalidation to confirm validity.
 *
 * Storage key: `proof-session-cache` — follows the `proof-*` namespace used
 * by all other localStorage keys in this codebase (proof-theme, proof-appearance,
 * proof-custom-theme). See useTheme.ts for the convention.
 *
 * Cache strategy: stale-while-revalidate.
 *   - Fresh (age < TTL_MS): render from cache, fire background revalidation.
 *   - Stale (age >= TTL_MS): render from cache immediately, fire revalidation,
 *     treat result as authoritative — better a brief stale render than a blank
 *     loading state.
 *   - Missing / corrupt: fall back to network fetch, same as before this PR.
 *
 * Invalidation:
 *   - Explicit sign-out: call clearSessionCache() before authClient.logout().
 *   - Multi-tab sign-out: the `storage` event fires on every other tab when
 *     one tab clears the key; the listener calls the registered on-clear
 *     callback so the tab can re-fetch and sign out.
 *   - 401 from revalidation: the caller may choose to clear the cache and
 *     redirect. The cache itself does not auto-clear on 401 — the rendered
 *     state should remain visible until an explicit decision is made.
 *
 * Edge cases defended:
 *   - JSON.parse corruption: clear the entry, return null.
 *   - System-clock drift (|now - cachedAt| > MAX_DRIFT_MS): treat as stale.
 *   - No `window` (SSR / prerender stubs): all functions are window-guarded.
 *
 * Architecture.md §4.6 — PWA offline-first. This cache is a warm-path
 * optimisation only; it does not replace the real session check.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * The subset of SessionState that is worth caching. Matches the "authenticated"
 * branch of SessionState in app.tsx, plus a null sentinel for "unauthenticated".
 * network-error is never cached — those states should always retry on mount.
 */
export type CacheableSession =
  | {
      kind: "authenticated";
      userId: string;
      email: string;
      name: string | null;
      onboardingCompletedAt: number | null;
      handle: string | null;
      /**
       * Server-derived admin flag from the get-session payload (A9 fix, QA
       * 2026-06-11). Optional for backward compatibility with cache entries
       * written before the field existed — useIsAdmin treats `undefined` as
       * "unknown, re-fetch", never as a false positive.
       */
      isAdmin?: boolean;
      /**
       * Server-authoritative plan tier from the get-session payload
       * (entitlements v0). Optional for backward compatibility with cache
       * entries written before the field existed — useTier treats `undefined`
       * as "unknown, re-fetch", defaulting to `free`. Display-only: the tier is
       * never the gate (the server enforces at the write path); a stale cached
       * value only delays the Settings → Plan display by one session refresh.
       */
      tier?: "free" | "paid";
      /**
       * Server-authoritative four-plan id from the get-session payload
       * (Entitlements four-tier amendment, C-WP0). Optional for backward
       * compatibility with entries written before the field existed — usePlanId
       * treats `undefined` as "unknown, re-fetch", defaulting to `free`.
       * Display-only, never the gate. The union is inlined (not imported from
       * useTier) to avoid a type import cycle, exactly as `tier` above.
       */
      planId?: "free" | "plus" | "pro" | "max";
    }
  | { kind: "unauthenticated" };

export interface SessionCacheEntry {
  session: CacheableSession;
  cachedAt: number; // Date.now() at write time
  ttlMs: 300000; // literal — not configurable at this PR
}

// ── Constants ─────────────────────────────────────────────────────────────────

export const SESSION_CACHE_KEY = "proof-session-cache" as const;
export const SESSION_CACHE_TTL_MS = 300_000; // 300 s (5 min)
/**
 * Maximum plausible age for a valid cache entry. Entries older than this
 * (or with a `cachedAt` in the future beyond this margin) are treated as stale
 * regardless of the TTL — defends against system-clock jumps.
 */
const MAX_DRIFT_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ── Internal helpers ──────────────────────────────────────────────────────────

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Read the cached session entry from localStorage.
 * Returns null on cache miss, JSON corruption, or non-browser environment.
 * On corruption, the bad entry is cleared so subsequent writes land cleanly.
 */
export function readSessionCache(): SessionCacheEntry | null {
  if (!isBrowser()) return null;
  const raw = window.localStorage.getItem(SESSION_CACHE_KEY);
  if (raw === null) return null;
  try {
    const entry = JSON.parse(raw) as SessionCacheEntry;
    // Structural guard — ensure the mandatory fields are present.
    if (
      typeof entry !== "object" ||
      entry === null ||
      typeof entry.cachedAt !== "number" ||
      entry.ttlMs !== SESSION_CACHE_TTL_MS
    ) {
      window.localStorage.removeItem(SESSION_CACHE_KEY);
      return null;
    }
    return entry;
  } catch {
    console.warn("[proof.session-cache] corrupt entry cleared");
    window.localStorage.removeItem(SESSION_CACHE_KEY);
    return null;
  }
}

/**
 * Returns true when the entry is fresh (within TTL, and not suspiciously old).
 */
export function isSessionCacheFresh(entry: SessionCacheEntry): boolean {
  const now = Date.now();
  const age = now - entry.cachedAt;
  // Reject if the entry is from the future (clock skew) or impossibly old.
  if (age < -MAX_DRIFT_MS || age > MAX_DRIFT_MS) return false;
  return age < entry.ttlMs;
}

/**
 * Write a session to localStorage.
 * Silently swallows QuotaExceededError — the cache is a warm-path optimisation;
 * the app degrades gracefully to a network fetch if the write fails.
 */
export function writeSessionCache(session: CacheableSession): void {
  if (!isBrowser()) return;
  const entry: SessionCacheEntry = {
    session,
    cachedAt: Date.now(),
    ttlMs: SESSION_CACHE_TTL_MS,
  };
  try {
    window.localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(entry));
  } catch (err) {
    console.warn("[proof.session-cache] write failed", err);
  }
}

/**
 * Remove the cached session. Call before authClient.logout() so any concurrent
 * tab that is listening via the storage event gets a clean invalidation signal.
 */
export function clearSessionCache(): void {
  if (!isBrowser()) return;
  window.localStorage.removeItem(SESSION_CACHE_KEY);
}

/**
 * Register a listener that fires when another tab clears the session cache
 * (multi-tab sign-out). Returns an unsubscribe function.
 *
 * The `storage` event only fires on OTHER tabs (not the one that made the write).
 * So tab A clearing the cache on sign-out triggers tab B's listener — tab B then
 * re-fetches and signs out if the session is gone.
 */
export function onSessionCacheCleared(callback: () => void): () => void {
  if (!isBrowser()) return () => {};

  const handler = (e: StorageEvent) => {
    if (e.key === SESSION_CACHE_KEY && e.newValue === null) {
      callback();
    }
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
